import { useEffect, useState, useCallback, useRef } from 'react';
import { createIdleManager, IdleManager } from './idleManager';

interface UseIdleDetectionOptions {
  idleTime?: number; // Time in milliseconds before considered idle (default: 5 minutes)
  events?: string[]; // Events to listen to for activity
}

/**
 * Hook to detect when user is idle
 * Uses the pure idleManager for business logic, making it more testable
 */
export const useIdleDetection = (options: UseIdleDetectionOptions = {}) => {
  const {
    idleTime = 1000 * 60 * 5,
    events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ],
  } = options;
  const [isIdle, setIsIdle] = useState(false);
  const managerRef = useRef<IdleManager | null>(null);

  useEffect(() => {
    // Create idle manager with pure function
    managerRef.current = createIdleManager(idleTime, {
      onIdle: () => setIsIdle(true),
      onActive: () => setIsIdle(false),
    });

    // Add event listeners that reset the timer
    const eventHandlers = events.map((event) => {
      const handler = () => {
        managerRef.current?.reset();
      };
      window.addEventListener(event, handler, { passive: true });
      return { event, handler };
    });

    // Cleanup
    return () => {
      managerRef.current?.cleanup();
      eventHandlers.forEach(({ event, handler }) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [events, idleTime]);

  const reset = useCallback(() => {
    managerRef.current?.reset();
  }, []);

  return {
    isIdle,
    reset,
    lastActivity: managerRef.current?.getLastActivity() ?? Date.now(),
  };
};
